import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    RowData,
    TableOptions,
    useReactTable
} from '@tanstack/react-table';

import {
    TableRoot,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../ui/TableUI';
import React, { useEffect, useRef, useState } from 'react';
import SelectPageSize from './SelectPageSize';
import { cn } from '@/utils/utils';
import PagingInfo from './PagingInfo';
import Paginate from '../pagination/Paginate';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/hooks/useStore';
import { SkeletonDataTable } from './SkeletonTable';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDownIcon } from 'lucide-react';
import ScrollTableGuide from '../general/ScrollTableGuide';
declare module '@tanstack/react-table' {
    // eslint-disable-next-line
    interface ColumnMeta<TData extends RowData, TValue> {
        headerClassName: string,
        cellClassName: string
    }
}

interface TableProps<TData, TValue> extends Partial<TableOptions<TData>> {
    columns: ColumnDef<TData, TValue>[],
    data: TData[],
    containerClassName?: string,
    headerClassName?: string,
    cellClassName?: string,
    noData?: React.ReactNode,
    isManualAll?: boolean,
    showTableFooter?: boolean,
    showPagination?: boolean,
    showPagingInfo?: boolean,
    showSelectPageSize?: boolean,
    loadingData?: boolean,
    selectPageSizes?: number[],
    renderActions?: React.ReactNode,
    enableScrollToTopTable?: boolean,
    enableScrollResTable?: boolean
}

export default observer(function Table<TData, TValue>({
    columns,
    data,
    containerClassName,
    headerClassName,
    cellClassName,
    noData,
    isManualAll = true,
    showTableFooter = true,
    showPagination = true,
    showPagingInfo = true,
    showSelectPageSize = true,
    loadingData = false,
    selectPageSizes,
    // select row
    enableRowSelection = false,
    renderActions,
    enableScrollToTopTable = true,
    enableScrollResTable = false,
    ...props
}: TableProps<TData, TValue>) {
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    // variables
    const columnSelect: ColumnDef<TData> = {
        accessorKey: 'select-box',
        header: ({ table }) => (
            <IndeterminateCheckbox
                {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler()
                }}
            />
        ),
        cell: ({ row }) => (
            <div>
                <IndeterminateCheckbox
                    {...{
                        checked: row.getIsSelected(),
                        disabled: !row.getCanSelect(),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: row.getToggleSelectedHandler()
                    }}
                />
            </div>
        ),
        meta: { cellClassName: 'sticky left-0', headerClassName: 'sticky left-0' },
        enableSorting: false,
        minSize: 10,
        size: 10,
        maxSize: 10
    };

    // hooks
    const { t } = useTranslation();

    // store
    const { apiStore: { isLoadingTable } } = useStore();

    // lifecycle
    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            const checkScrollable = () => {
                const hasScroll = el.scrollWidth > el.clientWidth;
                setShowHint(hasScroll);
            };

            requestAnimationFrame(() => {
                checkScrollable();
            });

            const handleScroll = () => {
                if (el.scrollLeft > 0) {
                    if (onFirstScroll.current === true) onFirstScroll.current = false;
                    setShowHint(false);
                }
            };

            el.addEventListener('scroll', handleScroll);
            // window.addEventListener('resize', checkScrollable);

            return () => {
                el.removeEventListener('scroll', handleScroll);
                // window.removeEventListener('resize', checkScrollable);
            };
        }
    }, [data]);

    // convert columns
    const columnsConvert = columns.map((column) => {
        switch (column?.id) {
            case 'id':
                return {
                    ...column,
                    meta: column.meta || { cellClassName: `${!!enableRowSelection ? 'sticky left-[45px]' : 'sticky left-0'}`, headerClassName: `${!!enableRowSelection ? 'sticky left-[45px]' : 'sticky left-0'}` }
                };
            // case 'actions':
            //     return {
            //         ...column,
            //         meta: column.meta || { cellClassName: 'sticky right-0', headerClassName: 'sticky right-0' }
            //     };
            default:
                return column;
        }
    });

    // init table
    const table = useReactTable({
        data,
        columns: !enableRowSelection ?
            columnsConvert :
            [
                columnSelect,
                ...columnsConvert
            ],
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection,
        ...(isManualAll ?
            {
                manualExpanding: true,
                manualFiltering: true,
                manualGrouping: true,
                manualPagination: true,
                manualSorting: true
            } :
            {}),
        ...props
    });

    return (
        <div className={containerClassName} ref={tableRef}>
            <div
                className={cn(
                    'flex flex-wrap-reverse justify-between gap-4 xl:gap-6',
                    (showSelectPageSize || renderActions) && 'mb-5'
                )}
            >
                {
                    showSelectPageSize && (
                        <div>
                            <SelectPageSize
                                pageSize={table.getState().pagination.pageSize}
                                setPagination={table.setPagination}
                                selectPageSizes={selectPageSizes}
                            />
                        </div>
                    )
                }
                {renderActions && <div>{renderActions}</div>}
            </div>
            <div className='relative'>
                <ScrollTableGuide showHint={enableScrollResTable === true && showHint && onFirstScroll.current === true} />
                <TableRoot scrollWrapperRef={scrollRef}>
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                'whitespace-nowrap',
                                                header.column.getCanSort() && 'cursor-pointer select-none',
                                                headerClassName,
                                                header.column.columnDef.meta?.headerClassName
                                            )}
                                            style={{ width: header.getSize() }}
                                            onClick={header.column.getToggleSortingHandler()}
                                            colSpan={header.colSpan}
                                            title={
                                                header.column.getCanSort() ?
                                                    header.column.getNextSortingOrder() === 'asc' ?
                                                        t('table.sort.ascending') :
                                                        header.column.getNextSortingOrder() === 'desc' ?
                                                            t('table.sort.descending') :
                                                            t('table.sort.clear') :
                                                    undefined
                                            }
                                        >
                                            {
                                                header.isPlaceholder ?
                                                    null :
                                                    (
                                                        <div className={cn(
                                                            'flex items-center justify-start gap-1'
                                                        )}
                                                        >
                                                            {
                                                                flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )
                                                            }
                                                            {
                                                                header.column.getCanSort() &&
                                                                (
                                                                    {
                                                                        asc: <ArrowUp className='inline-block w-4 h-4' />,
                                                                        desc: <ArrowDown className='inline-block w-4 h-4' />
                                                                    }[header.column.getIsSorted() as string] ?? <ArrowUpDownIcon className='inline-block w-4 h-4' />
                                                                )
                                                            }
                                                        </div>
                                                    )
                                            }
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    {
                        (!loadingData || (loadingData && !isLoadingTable)) && (
                            <TableBody>
                                {table.getRowModel().rows?.length > 0 ?
                                    (
                                        table.getRowModel().rows.map(row => (
                                            <TableRow
                                                key={row.id}
                                                data-state={row.getIsSelected() && 'selected'}
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <TableCell
                                                        key={cell.id}
                                                        className={cn(
                                                            'text-left',
                                                            cellClassName,
                                                            cell.column.columnDef.meta?.cellClassName
                                                        )}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) :
                                    (
                                        <TableRow>
                                            <TableCell colSpan={enableRowSelection ? columns?.length + 1 : columns?.length} className='h-36 text-center'>
                                                {noData || t('sentences.no_data')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                            </TableBody>
                        )
                    }
                    {
                        loadingData && isLoadingTable && <SkeletonDataTable numCols={table.getAllColumns()?.length} />
                    }
                </TableRoot>
            </div>
            {
                (!loadingData || (loadingData && !isLoadingTable)) && showTableFooter && table.getRowCount() > 0 && (
                    <div className='mt-4 flex justify-between items-center'>
                        <div>
                            {
                                showPagingInfo && (
                                    <PagingInfo
                                        pageIndex={table.getState().pagination.pageIndex}
                                        pageSize={table.getState().pagination.pageSize}
                                        totalRecords={table.getRowCount()}
                                    />
                                )
                            }
                        </div>
                        <div>
                            {
                                showPagination && (
                                    <Paginate
                                        forcePage={table.getState().pagination.pageIndex}
                                        pageCount={table.getPageCount()}
                                        onPageChange={({ selected }) => {
                                            table.setPageIndex(selected);

                                            // scroll to table
                                            if (enableScrollToTopTable && tableRef.current) {
                                                const top = tableRef.current.offsetTop - 15;
                                                window.scrollTo({
                                                    top,
                                                    behavior: 'smooth'
                                                });
                                            }
                                        }}
                                        marginPagesDisplayed={2}
                                    />
                                )
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
});

function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: { indeterminate?: boolean } & React.HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    return (
        <input
            type='checkbox'
            ref={ref}
            className={className + ' cursor-pointer'}
            {...rest}
        />
    );
}
